// Modify-op planner dispatcher. Routes the eight modify ops covered by the
// "modify" slice to per-op planners. The simpler text-replace ops
// (modify-where, modify-join-on, modify-select-window, toggle-select-distinct,
// rename-select-alias) live in this file; modify-join-kind, modify-limit and
// modify-update-assignment have their own helper modules.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Structural Edit Applicator — modify.

import type { PlannedByteEdit, StructuralEdit } from '../types';

import { rangeFromOffsets } from '../../sql-grammar/document/lineIndex';

import type { EditCtx } from './applyStructuralEdit';
import {
  deleteEdit,
  findKeyword,
  insertEdit,
  scanStatementKeywords,
  trimLeadingWs,
  trimTrailingWs,
} from './applyStructuralEdit.shared';
import { planJoinKindEdit } from './applyStructuralEdit.modify.joinKind';
import { planLimitEdit } from './applyStructuralEdit.modify.limit';
import { planUpdateAssignmentEdit } from './applyStructuralEdit.modify.update';
import { planRenameAliasEdit } from './applyStructuralEdit.modify.alias';
import { outsideLineComments, parenthesizeCompound } from './wholePredicate';

export type ModifyEdit = Extract<
  StructuralEdit,
  {
    op:
      | 'modify-where'
      | 'modify-having'
      | 'modify-join-on'
      | 'modify-join-kind'
      | 'modify-select-window'
      | 'toggle-select-distinct'
      | 'rename-select-alias'
      | 'modify-limit'
      | 'modify-update-assignment';
  }
>;

export function planModifyEdit(ctx: EditCtx, edit: ModifyEdit): readonly PlannedByteEdit[] {
  switch (edit.op) {
    case 'modify-where':
      return planModifyWhere(ctx, edit);
    case 'modify-having':
      return planModifyHaving(ctx, edit);
    case 'modify-join-on':
      return planModifyJoinOn(ctx, edit);
    case 'modify-join-kind':
      return planJoinKindEdit(ctx, edit);
    case 'modify-select-window':
      return planModifySelectWindow(ctx, edit);
    case 'toggle-select-distinct':
      return planToggleDistinct(ctx, edit);
    case 'rename-select-alias':
      return planRenameAliasEdit(ctx, edit);
    case 'modify-limit':
      return planLimitEdit(ctx, edit);
    case 'modify-update-assignment':
      return planUpdateAssignmentEdit(ctx, edit);
  }
}

function planModifyWhere(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'modify-where' }>,
): readonly PlannedByteEdit[] {
  const where = ctx.structure.where ?? [];
  if (edit.fragmentIndex < 0 || edit.fragmentIndex >= where.length) return [];
  const frag = where[edit.fragmentIndex];
  // Where-fragment ranges are already trimmed (parseBooleanFragments uses
  // trimRange) — replace verbatim; a multi-part predicate stays one fragment,
  // and a trailing line comment never swallows the rest of the clause.
  const { startOffset, endOffset } = frag.range;
  return [{
    range: frag.range,
    replacement: outsideLineComments(ctx, startOffset, endOffset, parenthesizeCompound(edit.newText, ctx.options)),
    rationale: 'modify-where',
  }];
}

function planModifyHaving(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'modify-having' }>,
): readonly PlannedByteEdit[] {
  const having = ctx.structure.having ?? [];
  if (edit.fragmentIndex < 0 || edit.fragmentIndex >= having.length) return [];
  const frag = having[edit.fragmentIndex];
  // Having-fragment ranges are already trimmed (flattenBooleanTree trims each
  // fragment boundary, same as WHERE) — replace verbatim; a multi-part
  // predicate stays one fragment, and a trailing line comment never swallows
  // the rest of the clause.
  const { startOffset, endOffset } = frag.range;
  return [{
    range: frag.range,
    replacement: outsideLineComments(ctx, startOffset, endOffset, parenthesizeCompound(edit.newText, ctx.options)),
    rationale: 'modify-having',
  }];
}

function planModifyJoinOn(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'modify-join-on' }>,
): readonly PlannedByteEdit[] {
  const joins = ctx.structure.joins ?? [];
  if (edit.index < 0 || edit.index >= joins.length) return [];
  const join = joins[edit.index];
  const on = join.on ?? [];
  if (on.length === 0) return [];
  // The first ON fragment's range starts at `tok.end` of 'ON' (so leading
  // whitespace is INSIDE the range) and the last fragment's range ends at the
  // bytes before the next clause keyword (no AND-split since the join body
  // captures the whole predicate as one fragment in v1). Trim ws on both
  // sides before replacing so we don't collapse the surrounding spaces.
  const startTrimmed = trimLeadingWs(ctx.source, on[0].range.startOffset, on[on.length - 1].range.endOffset);
  const endTrimmed = trimTrailingWs(ctx.source, startTrimmed, on[on.length - 1].range.endOffset);
  // A delete of [startTrimmed, endTrimmed) followed by a zero-width insert at
  // startTrimmed is exactly equivalent to ONE replace of the same range — emit
  // it as a single PlannedByteEdit (mirrors planModifyWhere's shape). The
  // SqlRange is built via the same rangeFromOffsets helper deleteEdit uses, so
  // its line/col fields are populated identically.
  return [{
    range: rangeFromOffsets(ctx.lineIdx, startTrimmed, endTrimmed),
    replacement: edit.newOnText,
    rationale: 'modify-join-on',
  }];
}

function planModifySelectWindow(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'modify-select-window' }>,
): readonly PlannedByteEdit[] {
  const items = ctx.structure.selectItems ?? [];
  if (edit.selectItemIndex < 0 || edit.selectItemIndex >= items.length) return [];
  const item = items[edit.selectItemIndex];
  if (!item.window) return [];
  return [{
    range: item.window.range,
    replacement: edit.newOverText,
    rationale: 'modify-select-window',
  }];
}

function planToggleDistinct(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'toggle-select-distinct' }>,
): readonly PlannedByteEdit[] {
  const isDistinct = ctx.structure.distinct === true;
  if (edit.distinct === isDistinct) return [];
  const kws = scanStatementKeywords(ctx);
  if (edit.distinct) {
    const select = findKeyword(kws, 'SELECT');
    if (!select) return [];
    return [insertEdit(ctx, select.end, ' DISTINCT', 'toggle-select-distinct:on')];
  }
  const distinctKw = findKeyword(kws, 'DISTINCT');
  if (!distinctKw) return [];
  // Drop DISTINCT plus one trailing whitespace.
  let end = distinctKw.end;
  if (end < ctx.source.length && /\s/.test(ctx.source[end])) end += 1;
  return [deleteEdit(ctx, distinctKw.start, end, 'toggle-select-distinct:off')];
}
